import { type ChangeEvent, useState } from "react";

import { useHistory } from "react-router-dom";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { handleResponseError } from "../../../../api/axiosClient";
import { useApiContext } from "../../../../api/context";
import { helpURLs } from "../../../../assets/urls";
import LoadingButton from "../../../../Components/LoadingButton";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { UpdateErrorAlert } from "./Alerts/UpdateErrorAlert";

const DOIT_ACCOUNT_ID = "068664126052";
const ERRORS = {
  S3_BUCKET_REQUIRED: "S3 bucket name is required when a region is selected",
  REGION_REQUIRED: "Region is required when an S3 bucket name is provided",
};

/**
 * Component to handle adding account manually by following documentation
 */
export const AddArn = () => {
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const [arn, setArn] = useState<string>("");
  const [s3Bucket, setS3Bucket] = useState<string>("");
  const [region, setRegion] = useState<string>("");
  const [loadingArn, setLoadingArn] = useState<boolean>(false);
  const [s3BucketError, setS3BucketError] = useState<string>("");
  const [regionError, setRegionError] = useState<string>("");
  const history = useHistory();

  const awsS3BucketRegions = ["eu-central-1", "eu-west-1", "us-east-1", "us-east-2", "us-west-2"].sort();

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);

  const onChangeArnInput = (event: ChangeEvent<HTMLInputElement>) => {
    setArn(event.target.value);
    setShowError(false);
  };

  const onChangeS3BucketInput = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setS3Bucket(value);

    if (value) {
      setS3BucketError("");
    } else if (region) {
      setS3BucketError(ERRORS.S3_BUCKET_REQUIRED);
    }
  };

  const onChangeRegion = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setRegion(value);

    if (value) {
      setRegionError("");
    } else if (s3Bucket) {
      setRegionError(ERRORS.REGION_REQUIRED);
    }
  };

  const clearRegion = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRegion("");

    if (s3Bucket) {
      setRegionError(ERRORS.REGION_REQUIRED);
    }
  };

  const validateFields = (): boolean => {
    let isValid = true;

    if (s3Bucket && !region) {
      setRegionError(ERRORS.REGION_REQUIRED);
      isValid = false;
    }

    if (region && !s3Bucket) {
      setS3BucketError(ERRORS.S3_BUCKET_REQUIRED);
      isValid = false;
    }

    return isValid;
  };

  const handleAddArn = async () => {
    if (!validateFields()) {
      return;
    }

    setLoadingArn(true);

    try {
      await api.post(`/v1/customers/${customer.id}/cloudconnect/amazon-web-services`, {
        arn,
        s3Bucket,
        s3BucketRegion: region,
      });

      history.goBack();
      setLoadingArn(false);
    } catch (error: any) {
      handleResponseError(error, (err) => {
        setErrorMessage(err);
        setShowError(true);
        setLoadingArn(false);
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          pb: 2,
        }}
      >
        <Link
          data-cy="how-to-link-aws-account-link"
          href={helpURLs.ADD_AWS_IAM_ROLE}
          target="_blank"
          variant="subtitle1"
          rel="noopener noreferrer"
        >
          How To Link Your AWS Account
        </Link>
      </Box>
      <Box
        sx={{
          display: "flex",
        }}
      >
        <Box>
          <Typography data-cy="account-external-id-box" variant="body1">
            Our AWS Account:
            <br />
            Your External ID:
          </Typography>
        </Box>
        <Box
          sx={{
            pl: 1,
          }}
        >
          <Typography variant="body1" color="textSecondary">
            {DOIT_ACCOUNT_ID}
            <br />
            {customer.id}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          pt: 3,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
          }}
        >
          <TextField
            data-cy="add-arn-text-field"
            id="outlined-name"
            label="Role ARN"
            variant="outlined"
            value={arn}
            onChange={onChangeArnInput}
            fullWidth
          />
        </Box>
      </Box>

      <Box sx={{ pt: 4 }}>
        <Typography variant="body1" sx={{ pb: 2 }}>
          The following is required to enable real-time anomalies:
        </Typography>

        <Box sx={{ pb: 2 }}>
          <TextField
            data-cy="s3-bucket-text-field"
            id="s3-bucket-name"
            label="S3 bucket name"
            variant="outlined"
            value={s3Bucket}
            onChange={onChangeS3BucketInput}
            fullWidth
            size="small"
            error={!!s3BucketError}
            helperText={s3BucketError}
            required={!!region}
          />
        </Box>

        <FormControl fullWidth variant="outlined" size="small" error={!!regionError} required={!!s3Bucket}>
          <InputLabel id="region-label">Region</InputLabel>
          <Select
            data-cy="region-select"
            labelId="region-label"
            id="region"
            value={region}
            onChange={onChangeRegion}
            label="Region"
            endAdornment={
              region ? (
                <InputAdornment position="end" sx={{ marginRight: "24px" }}>
                  <IconButton aria-label="clear region selection" onClick={clearRegion} edge="end" size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          >
            {awsS3BucketRegions.map((region) => (
              <MenuItem key={region} value={region}>
                {region}
              </MenuItem>
            ))}
          </Select>
          {regionError && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {regionError}
            </Typography>
          )}
        </FormControl>
      </Box>

      <Box sx={{ mt: 2, mb: 8 }}>
        <Collapse in={showError}>
          <UpdateErrorAlert
            error={errorMessage}
            onClose={() => {
              setShowError(false);
            }}
          />
        </Collapse>
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: "60px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 4,
        }}
      >
        <Divider sx={{ position: "absolute", top: 0, width: "100%" }} />
        <Box
          sx={{
            width: "100%",
            maxWidth: 1100,
            display: "flex",
            justifyContent: "flex-end",
            pr: 2,
          }}
        >
          <LoadingButton
            data-cy="add-arn-button"
            color="primary"
            variant="contained"
            loading={loadingArn}
            onClick={handleAddArn}
            disabled={loadingArn || !arn.length || (!!s3Bucket && !region) || (!!region && !s3Bucket)}
            mixpanelEventId="settings.arn.add"
          >
            Add
          </LoadingButton>
        </Box>
      </Box>
    </>
  );
};
